import { Grid, Typography } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import FormSection from "../../../../../common/components/functional/formSection/FormSection";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../../../app/hooks";
import SwitchField from "../../../../../common/components/functional/switch/SwitchField";
import { AssignmentById } from "../../../../talents/talentAPI";
import { changeAssignmentOrderAttributes } from "../../../../talents/talentsSlice";
import { OrderAttributesFormValues } from "../types";

type Props = {
  assignment: AssignmentById;
  canEdit: boolean;
};

const OrderAttributes = ({ assignment, canEdit }: Props) => {
  const [edit, setEdit] = useState(false);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const methods = useForm<OrderAttributesFormValues>({
    defaultValues: {
      ...assignment?.details?.orderAttributes,
      isCustomerRecruit: assignment?.details?.isCustomerRecruit || false,
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty, isValid },
  } = methods;

  useEffect(() => {
    reset({
      ...assignment?.details?.orderAttributes,
      isCustomerRecruit: assignment.details?.isCustomerRecruit || false,
    });
  }, [
    assignment?.details?.orderAttributes,
    assignment?.details?.isCustomerRecruit,
    reset,
  ]);

  const onSave = (fields: OrderAttributesFormValues) => {
    const params = {
      assignmentId: assignment?.assignmentId,
      version: assignment?.details?.version,
      orderAttributes: {
        isExpress: fields.isExpress,
        isSelfService: fields.isSelfService,
        isEarlyBird: fields.isEarlyBird,
      },
      isCustomerRecruit: fields.isCustomerRecruit,
    };
    dispatch(changeAssignmentOrderAttributes(params));
  };

  const onEdit = () => {
    setEdit(!edit);

    if (edit) handleSubmit(onSave)();
  };

  const onCancel = () => {
    setEdit(false);
    reset();
  };

  const saveDisabled = !isDirty || !isValid;

  return (
    <FormSection
      title={t("talentProfile:orderAttributes")}
      edit={edit}
      onEdit={onEdit}
      onCancel={onCancel}
      saveDisabled={saveDisabled}
      readOnly={!canEdit}
    >
      <FormProvider {...methods}>
        <Grid xs={2} item alignSelf="center" justifyItems="end">
          <Typography color="text.secondary" textAlign="end">
            {t("talentProfile:expressOrder")}
          </Typography>
        </Grid>
        <Grid xs={10} item>
          <SwitchField name="isExpress" disabled={!edit} />
        </Grid>
        <Grid xs={2} item alignSelf="center" justifyItems="end">
          <Typography color="text.secondary" textAlign="end">
            {t("talentProfile:officeOrder")}
          </Typography>
        </Grid>
        <Grid xs={10} item>
          <SwitchField name="isSelfService" disabled={!edit} reversedValue />
        </Grid>
        <Grid xs={2} item alignSelf="center" justifyItems="end">
          <Typography color="text.secondary" textAlign="end">
            {t("talentProfile:earlyBirdOrder")}
          </Typography>
        </Grid>
        <Grid xs={10} item>
          <SwitchField name="isEarlyBird" disabled={!edit} />
        </Grid>
        <Grid xs={2} item alignSelf="center" justifyItems="end">
          <Typography color="text.secondary" textAlign="end">
            {t("talentProfile:customerRecruit")}
          </Typography>
        </Grid>
        <Grid xs={10} item>
          <SwitchField name="isCustomerRecruit" disabled={!edit} />
        </Grid>
      </FormProvider>
    </FormSection>
  );
};

export default OrderAttributes;
